/**
 * MermaidDiagram Component
 * 
 * Renders a Mermaid flowchart diagram.
 * Uses mermaid.js library to render the diagram from code.
 */

import React, { useEffect, useRef, useState } from 'react';
import { ProcessDiagram } from '../../../types';

interface MermaidDiagramProps {
  diagram: ProcessDiagram;
  className?: string;
}

export const MermaidDiagram: React.FC<MermaidDiagramProps> = ({ diagram, className = '' }) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const [svgContent, setSvgContent] = useState<string>('');
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const diagramIdRef = useRef<string>(`mermaid-${Date.now()}-${Math.random().toString(36).slice(2)}`);

  useEffect(() => {
    let isMounted = true;

    const renderDiagram = async () => {
      if (!diagram.mermaidCode) {
        return;
      }

      setIsLoading(true);
      setError(null);
      setSvgContent('');

      try {
        // Dynamically import mermaid
        const mermaid = await import('mermaid');
        
        // Initialize mermaid with better styling
        mermaid.default.initialize({ 
          startOnLoad: false,
          theme: 'base',
          themeVariables: {
            primaryColor: '#6366f1',
            primaryTextColor: '#1e293b',
            primaryBorderColor: '#6366f1',
            lineColor: '#94a3b8',
            secondaryColor: '#e0e7ff',
            tertiaryColor: '#f1f5f9',
            background: '#ffffff',
            mainBkg: '#e0e7ff',
            secondBkg: '#f1f5f9',
            tertiaryBkg: '#fef3c7',
            nodeBorder: '#6366f1',
            clusterBkg: '#f8fafc',
            clusterBorder: '#cbd5e1',
            fontSize: '16px',
            fontFamily: 'system-ui, -apple-system, sans-serif'
          },
          securityLevel: 'loose',
          flowchart: {
            useMaxWidth: true,
            htmlLabels: true,
            curve: 'basis',
            padding: 20,
            nodeSpacing: 50,
            rankSpacing: 50,
            diagramPadding: 20
          }
        });

        // Generate unique ID for this render
        const diagramId = `${diagramIdRef.current}-${Date.now()}`;

        // Render the diagram
        const { svg } = await mermaid.default.render(diagramId, diagram.mermaidCode);
        
        if (isMounted) {
          setSvgContent(svg);
          setIsLoading(false);
        }
      } catch (err) {
        console.error('Error rendering Mermaid diagram:', err);
        if (isMounted) {
          setError('Failed to render diagram');
          setIsLoading(false);
        }
      }
    };

    renderDiagram();

    return () => {
      isMounted = false;
    };
  }, [diagram.mermaidCode]);

  const containerStyles: React.CSSProperties = {
    padding: '1.75rem',
    background: 'linear-gradient(135deg, rgba(255, 255, 255, 0.9) 0%, rgba(248, 250, 252, 0.9) 100%)',
    borderRadius: '12px',
    border: '1px solid rgba(0, 0, 0, 0.08)',
    boxShadow: '0 4px 12px rgba(0, 0, 0, 0.05)',
    transition: 'all 0.2s ease',
  };

  const titleStyles: React.CSSProperties = {
    marginBottom: '0.75rem',
    fontSize: '1.35rem',
    fontWeight: '600',
    color: '#1a202c',
    display: 'flex',
    alignItems: 'center',
    gap: '0.5rem',
  };

  const descriptionStyles: React.CSSProperties = {
    marginBottom: '1.25rem',
    color: '#64748b',
    fontSize: '0.95rem',
    lineHeight: '1.6',
  };

  const diagramWrapperStyles: React.CSSProperties = {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    minHeight: '300px',
    backgroundColor: 'white',
    borderRadius: '10px',
    padding: '2rem',
    border: '1px solid rgba(0, 0, 0, 0.06)',
    overflow: 'auto',
  };

  return (
    <div className={className} style={containerStyles}>
      <h3 style={titleStyles}>
        <span style={{ color: '#6366f1' }}>📊</span>
        {diagram.title}
      </h3>
      {diagram.description && (
        <p style={descriptionStyles}>
          {diagram.description}
        </p>
      )}
      <div 
        ref={containerRef}
        className="mermaid-diagram"
        style={diagramWrapperStyles}
      >
        {isLoading && !error && (
          <div style={{ color: '#94a3b8', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
            <span>⏳</span> Loading diagram...
          </div>
        )}
        {error && (
          <div style={{ 
            padding: '1.5rem', 
            border: '1px solid #fee2e2', 
            borderRadius: '8px', 
            background: '#fef2f2',
            maxWidth: '100%'
          }}>
            <p style={{ color: '#991b1b', margin: '0 0 10px 0', fontWeight: '500' }}>
              ⚠️ Unable to render diagram
            </p>
            <details>
              <summary style={{ cursor: 'pointer', color: '#64748b', marginBottom: '0.5rem' }}>
                Show raw Mermaid code
              </summary>
              <pre style={{ 
                margin: 0, 
                padding: '12px', 
                background: 'white', 
                borderRadius: '6px', 
                overflowX: 'auto', 
                fontSize: '13px',
                border: '1px solid #e2e8f0'
              }}>
                {diagram.mermaidCode}
              </pre>
            </details>
          </div>
        )}
        {!isLoading && !error && svgContent && (
          <div 
            dangerouslySetInnerHTML={{ __html: svgContent }} 
            style={{ 
              width: '100%', 
              display: 'flex', 
              justifyContent: 'center' 
            }}
          />
        )}
      </div>
    </div>
  );
};

